import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';

function ReviewInbox({ classId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState('pending');
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [overrideNote, setOverrideNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const limit = 20;

  const loadItems = useCallback(async () => {
    if (!classId) return;
    setLoading(true);
    try {
      const data = await apiClient.get(
        `/classes/${classId}/review?status=${status}&limit=${limit}&offset=${offset}`
      );
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load review items:', err);
    } finally {
      setLoading(false);
    }
  }, [classId, status, offset]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const loadDetail = async (item) => {
    setSelectedItem(item);
    setDetailData(null);
    setOverrideNote('');
    setLoadingDetail(true);
    try {
      const data = await apiClient.get(`/classes/${classId}/review/${item.id}`);
      setDetailData(data);
    } catch (err) {
      console.error('Failed to load detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleOverride = async (isCorrect) => {
    if (!selectedItem) return;
    setSubmitting(true);
    try {
      await apiClient.post(`/classes/attempts/${selectedItem.id}/override`, {
        is_correct: isCorrect,
        note: overrideNote || null
      });
      setSelectedItem(null);
      setDetailData(null);
      loadItems(); // Refresh list
    } catch (err) {
      console.error('Failed to override:', err);
      alert('Viga salvestamisel: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('et-EE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatConfidence = (conf) => {
    if (conf === null || conf === undefined) return '-';
    return `${Math.round(conf * 100)}%`;
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div>
      {/* Modal for review detail */}
      {selectedItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '24px'
        }} onClick={() => setSelectedItem(null)}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            maxWidth: '1000px',
            maxHeight: '90vh',
            width: '100%',
            overflow: 'auto',
            padding: '24px'
          }} onClick={(e) => e.stopPropagation()}>
            {loadingDetail ? (
              <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
                Laadin...
              </div>
            ) : detailData ? (
              <>
                {/* Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '20px'
                }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>
                      {detailData.studentName}
                    </h3>
                    <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                      {formatTime(detailData.createdAt)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: detailData.isCorrectAI ? '#dcfce7' : '#fef2f2',
                      color: detailData.isCorrectAI ? '#166534' : '#b91c1c'
                    }}>
                      AI: {detailData.isCorrectAI ? 'ÕIGE' : 'VALE'}
                    </span>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: '#fef3c7',
                      color: '#92400e'
                    }}>
                      Kindlus: {formatConfidence(detailData.confidence)}
                    </span>
                    <button
                      onClick={() => setSelectedItem(null)}
                      style={{
                        background: '#f3f4f6',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      Sulge
                    </button>
                  </div>
                </div>

                {/* Images */}
                {detailData.taskImage && detailData.workImage && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '16px',
                    marginBottom: '20px'
                  }}>
                    <div>
                      <div style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#6b7280',
                        marginBottom: '8px',
                        textTransform: 'uppercase'
                      }}>
                        Ülesanne
                      </div>
                      <img
                        src={`data:image/jpeg;base64,${detailData.taskImage}`}
                        alt="Task"
                        style={{
                          width: '100%',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}
                      />
                    </div>
                    <div>
                      <div style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#6b7280',
                        marginBottom: '8px',
                        textTransform: 'uppercase'
                      }}>
                        Õpilase lahendus
                      </div>
                      <img
                        src={`data:image/jpeg;base64,${detailData.workImage}`}
                        alt="Student work"
                        style={{
                          width: '100%',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* AI Analysis */}
                <div style={{
                  background: '#f9fafb',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    marginBottom: '12px',
                    textTransform: 'uppercase'
                  }}>
                    AI Analüüs
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>
                        Õpilase vastus (AI luges)
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>
                        {detailData.extractedAnswer || '-'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>
                        Õige vastus (AI arvates)
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>
                        {detailData.expectedAnswer || '-'}
                      </div>
                    </div>
                  </div>
                  {detailData.feedback && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>
                        AI tagasiside õpilasele
                      </div>
                      <div style={{ fontSize: '14px' }}>
                        {detailData.feedback}
                      </div>
                    </div>
                  )}
                  {detailData.errorType && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>
                        Vea tüüp
                      </div>
                      <div style={{
                        fontSize: '12px',
                        padding: '4px 8px',
                        background: '#fef2f2',
                        color: '#b91c1c',
                        borderRadius: '4px',
                        display: 'inline-block'
                      }}>
                        {detailData.errorType}
                      </div>
                    </div>
                  )}
                </div>

                {/* Teacher Override Section */}
                {detailData.teacherOverride === null ? (
                  <div style={{
                    background: '#fffbeb',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #fcd34d'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '12px'
                    }}>
                      Õpetaja otsus
                    </div>
                    <textarea
                      value={overrideNote}
                      onChange={(e) => setOverrideNote(e.target.value)}
                      placeholder="Märkus (valikuline)..."
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        fontSize: '14px',
                        marginBottom: '12px',
                        minHeight: '60px',
                        resize: 'vertical'
                      }}
                    />
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={() => handleOverride(true)}
                        disabled={submitting}
                        style={{
                          flex: 1,
                          padding: '12px',
                          borderRadius: '8px',
                          background: '#22c55e',
                          color: 'white',
                          border: 'none',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: submitting ? 'not-allowed' : 'pointer',
                          opacity: submitting ? 0.6 : 1
                        }}
                      >
                        Märgi ÕIGEKS
                      </button>
                      <button
                        onClick={() => handleOverride(false)}
                        disabled={submitting}
                        style={{
                          flex: 1,
                          padding: '12px',
                          borderRadius: '8px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: submitting ? 'not-allowed' : 'pointer',
                          opacity: submitting ? 0.6 : 1
                        }}
                      >
                        Märgi VALEKS
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    background: '#f0fdf4',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #86efac'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '8px'
                    }}>
                      Õpetaja otsus: {detailData.teacherOverride ? 'ÕIGE' : 'VALE'}
                    </div>
                    {detailData.reviewNote && (
                      <div style={{ fontSize: '14px', color: '#374151' }}>
                        Märkus: {detailData.reviewNote}
                      </div>
                    )}
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Ülevaadatud: {formatTime(detailData.reviewedAt)}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
                Andmete laadimine ebaõnnestus
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button
          onClick={() => { setStatus('pending'); setOffset(0); }}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: status === 'pending' ? '2px solid #f59e0b' : '1px solid #e5e7eb',
            background: status === 'pending' ? '#fffbeb' : 'white',
            color: status === 'pending' ? '#92400e' : '#6b7280',
            fontWeight: '600',
            fontSize: '13px',
            cursor: 'pointer'
          }}
        >
          Ootel ({status === 'pending' ? total : '...'})
        </button>
        <button
          onClick={() => { setStatus('reviewed'); setOffset(0); }}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: status === 'reviewed' ? '2px solid #22c55e' : '1px solid #e5e7eb',
            background: status === 'reviewed' ? '#f0fdf4' : 'white',
            color: status === 'reviewed' ? '#166534' : '#6b7280',
            fontWeight: '600',
            fontSize: '13px',
            cursor: 'pointer'
          }}
        >
          Ülevaadatud
        </button>
        <button
          onClick={() => { setStatus('all'); setOffset(0); }}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: status === 'all' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
            background: status === 'all' ? '#eff6ff' : 'white',
            color: status === 'all' ? '#1d4ed8' : '#6b7280',
            fontWeight: '600',
            fontSize: '13px',
            cursor: 'pointer'
          }}
        >
          Kõik
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
          Laadin...
        </div>
      ) : items.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '48px',
          background: '#f9fafb',
          borderRadius: '12px',
          color: '#6b7280'
        }}>
          {status === 'pending' ? 'Pole ühtegi ülevaatamist ootavat katset' : 'Pole ühtegi katset'}
        </div>
      ) : (
        <>
          {/* Table */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Õpilane</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Aeg</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>AI otsus</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Kindlus</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Põhjus</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Staatus</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr
                    key={item.id}
                    style={{
                      borderTop: idx > 0 ? '1px solid #e5e7eb' : 'none',
                      cursor: 'pointer'
                    }}
                    onClick={() => loadDetail(item)}
                    onMouseOver={(e) => e.currentTarget.style.background = '#f9fafb'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>{item.studentName}</div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>
                      {formatTime(item.createdAt)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: item.isCorrectAI ? '#dcfce7' : '#fef2f2',
                        color: item.isCorrectAI ? '#166534' : '#b91c1c'
                      }}>
                        {item.isCorrectAI ? 'ÕIGE' : 'VALE'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px' }}>
                      <span style={{
                        color: item.confidence && item.confidence >= 0.7 ? '#166534' :
                               item.confidence && item.confidence >= 0.5 ? '#92400e' : '#b91c1c'
                      }}>
                        {formatConfidence(item.confidence)}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {item.errorType && (
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          background: '#fef3c7',
                          color: '#92400e'
                        }}>
                          {item.errorType}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {item.teacherOverride !== null ? (
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: '#dcfce7',
                          color: '#166534'
                        }}>
                          Ülevaadatud
                        </span>
                      ) : (
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: '#fef3c7',
                          color: '#92400e'
                        }}>
                          Ootel
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <button
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb',
                          background: 'white',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Vaata
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '12px',
              marginTop: '16px'
            }}>
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  cursor: offset === 0 ? 'not-allowed' : 'pointer',
                  opacity: offset === 0 ? 0.5 : 1
                }}
              >
                Eelmine
              </button>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>
                Leht {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={currentPage >= totalPages}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage >= totalPages ? 0.5 : 1
                }}
              >
                Järgmine
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ReviewInbox;
